import { limparMarcaDeSessao, salvarMarcaDeSessao } from '@/lib/preferencias';
import type { RespostaDeLogin } from '@/types/api';

/**
 * A sessao ativa, guardada em memoria do modulo.
 *
 * Especificacao V3.1, item 15.2.
 *
 * O ACCESS TOKEN NUNCA SAI DA MEMORIA. Ele morre ao recarregar a pagina, e isso
 * esta correto: dura 15 minutos e e obtido de novo pelo refresh token.
 *
 * O REFRESH TOKEN NAO PASSA POR AQUI. Ele vive em cookie HttpOnly, gravado pelo
 * servidor, ilegivel por JavaScript. O tipo abaixo remove o campo da resposta de
 * proposito: guardar o valor recebido no corpo reabriria, em memoria, o acesso que
 * o cookie fecha, e daria a impressao de que o cliente controla a rotacao.
 *
 * POR QUE FORA DO REACT
 *
 * O interceptor de 401 vive em `lib/http.ts` e e chamado de fora da arvore de
 * componentes. Duas copias do token — uma no modulo, outra em estado do React —
 * divergiriam justamente logo depois de uma renovacao, que e quando a diferenca
 * importa. O React assina esta store por `useSyncExternalStore`.
 */

/** Sessao ativa. A resposta do servidor MENOS o refresh token. */
export type SessaoAtiva = Omit<RespostaDeLogin, 'refreshToken'>;

let sessao: SessaoAtiva | null = null;

const ouvintes = new Set<() => void>();

/**
 * Descarta o refresh token que vem no corpo da resposta.
 *
 * Campo por campo, e nao `...resto`. Com espalhamento, um campo novo na resposta do
 * servidor entraria aqui sozinho — inclusive um campo sensivel. Assim, acrescentar
 * algo a sessao e uma decisao que passa por este arquivo.
 */
export function deRespostaDeLogin(resposta: RespostaDeLogin): SessaoAtiva {
  return {
    accessToken: resposta.accessToken,
    accessTokenExpiraEm: resposta.accessTokenExpiraEm,
    refreshTokenExpiraEm: resposta.refreshTokenExpiraEm,
    deveTrocarSenha: resposta.deveTrocarSenha,
    usuario: resposta.usuario,
  };
}

/** Le a sessao ativa. Referencia estavel entre alteracoes. */
export function lerSessao(): SessaoAtiva | null {
  return sessao;
}

/**
 * Substitui a sessao ativa e avisa quem estiver assinando.
 *
 * Mantem a marca de sessao em sincronia. E o unico ponto que a escreve, para que
 * nao exista caminho em que a sessao muda e a marca fica velha.
 */
export function definirSessao(nova: SessaoAtiva | null): void {
  sessao = nova;

  if (nova === null) {
    limparMarcaDeSessao();
  } else {
    // A marca acompanha o prazo do REFRESH token, nao o do access token: o que ela
    // responde e "vale tentar renovar?".
    salvarMarcaDeSessao(nova.refreshTokenExpiraEm);
  }

  for (const ouvinte of ouvintes) {
    ouvinte();
  }
}

/** Assina alteracoes da sessao. Devolve a funcao de cancelamento. */
export function assinarSessao(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);

  return () => {
    ouvintes.delete(ouvinte);
  };
}

/** Le o access token atual, ou nulo quando nao houver sessao. */
export function lerAccessToken(): string | null {
  return sessao?.accessToken ?? null;
}
